import { IconClipboardCheck } from "@tabler/icons-react";
import { useMemo } from "react";
import { useFormatMoney } from "~/modules/multi-currency/ui/format-money";
import { Box, Flex, Modal, Text } from "~/ui";
import type { ActiveReportArtifact } from "~/utils/types/assistant-report-artifact";

import { buildMethodologySections, methodologyFooterCopy } from "./query-methodology-view-model";

interface QueryMethodologyModalProps {
  opened: boolean;
  onClose: () => void;
  artifact: ActiveReportArtifact | null;
  isError?: boolean;
  errorMessage?: string;
}

export const QueryMethodologyModal = ({ opened, onClose, artifact, isError = false, errorMessage }: QueryMethodologyModalProps) => {
  const formatMoney = useFormatMoney();
  const sections = useMemo(() => (artifact ? buildMethodologySections(artifact, formatMoney) : []), [artifact, formatMoney]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Flex align="center" gap="xs">
          <IconClipboardCheck size={18} />
          <Text fw={600}>{artifact ? "Check My Work" : "Report details"}</Text>
        </Flex>
      }
      closeButtonProps={{ "aria-label": "Close Check My Work" }}
      size="lg"
      centered
    >
      <Flex direction="column" gap="md">
        {isError && artifact && (
          <Box p="sm" bg="red.0">
            <Text size="sm" c="red.7">
              Latest analysis failed; this report is unchanged
            </Text>
          </Box>
        )}
        {isError && !artifact && (
          <Box p="md" bg="red.0">
            <Text size="sm" c="red.7">
              {errorMessage || "The analysis could not be completed."}
            </Text>
          </Box>
        )}
        {sections.map(section => (
          <Box key={section.title}>
            <Text size="sm" fw={600} c="gray.8" mb={4}>
              {section.title}
            </Text>
            {section.lines.map(line => (
              <Text key={`${section.title}-${line}`} size="sm" c="gray.6">
                {line}
              </Text>
            ))}
          </Box>
        ))}
        {artifact && (
          <Text size="xs" c="gray.6">
            {methodologyFooterCopy(artifact.dataset.presentationManifest?.rowInteraction?.mode === "locked")}
          </Text>
        )}
      </Flex>
    </Modal>
  );
};
